/* eslint-disable no-console */
/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit';

import { getApi, putApi, postApi } from 'lib/axios';
import { PRODUCT_LIST_VIEW } from 'routes/urls';
import { BASE_URL, HTTP_STATUS } from 'utils/const';

import { enqueueSnackbar } from './notificationSlice';

const buildShippingCostParams = (shippingCosts = []) =>
  shippingCosts.map((shippingCost) => ({
    prefecture_id: Number(shippingCost.prefecture_id),
    postage: Number(shippingCost.postage),
  }));

/**
 * 商品を全件取得する
 */
export const fetchAllProducts = createAsyncThunk(
  'requests/fetchAllProducts',
  async (data, { dispatch }) => {
    const param = {
      per_page: data.rowsPerPage,
      page: data.page,
      item_title: data.itemName,
      car_model_name: data.modelNumber,
      genuine_part_number: data.genuine_part_number,
      supplier_item_key: data.supplierItemKey,
      exhibitor_user_name: data.exhibitorName,
      supplier_id: data.supplierId,
      maker_ids: data.makerIds ? data.makerIds.join(',') : undefined,
      parts_classifications: data.partsClassifications
        ? data.partsClassifications.join(',')
        : undefined,
      prefecture_ids: data.prefectureIds ? data.prefectureIds.join(',') : undefined,
      parts_ids: data.partsIds ? data.partsIds.join(',') : undefined,
      statuses: data.statuses || undefined,
      mileage_from: data.mileageFrom,
      mileage_to: data.mileageTo,
      sales_price_from: data.salesPriceFrom,
      sales_price_to: data.salesPriceTo,
    };

    try {
      const res = await getApi(
        `${BASE_URL}/items`,
        // ここに「params」というキー名でセットし、クエリパラメータを指定する
        param,
      );

      return {
        data: res.data,
        total: res.meta.total,
      }; // action.payloadとしてReducerで読み取れる
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );
      throw error;
    }
  },
);

/**
 * リビルト品以外の商品を取得する
 */
export const fetchNonRebuiltProduct = createAsyncThunk(
  'requests/fetchNonRebuiltProduct',
  async (id) => {
    const res = await getApi(`${BASE_URL}/items/${id}`);

    console.log('requestAPI : 接続', res);

    return { data: res.data };
  },
);

/**
 * リビルト品の商品を取得する
 */
export const fetchRebuiltProduct = createAsyncThunk('requests/fetchRebuiltProduct', async (id) => {
  const res = await getApi(`${BASE_URL}/items/rebuilt/${id}`);

  console.log('requestAPI : 接続', res);

  return { data: res.data };
});

/**
 * リビルト品以外の商品を更新する
 */
export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async (data, { dispatch }) => {
    try {
      // 生産年式を年と月に分割（例: "H30/1" → year="H30", month="1"）
      const [modelYear, modelMonth] = data?.data?.model_year?.split('/') || ['', ''];

      const params = {
        item_name: data?.data.item_name,
        car_model_designation_no: data?.data?.car_model_designation_no,
        car_model_classification: data?.data?.classification_no,
        car_model_name: data?.carsModel?.name,
        car_model_id: data?.carsModel?.id,
        maker_id: data?.data?.maker,
        car_id: data?.data?.car_name,
        vehicle_no: data?.data?.vehicle_no,
        model_year: modelYear,
        model_month: modelMonth,
        color: data?.data?.color?.id,
        trim: data?.data?.trim,
        grade_name: data?.data?.grade,
        engine: data?.engine?.id,
        mission: data?.data?.mission,
        drive_method: data?.data?.drive_method,
        frame: data?.data?.frame,
        mileage: data?.data?.mileage,
        parts_classification: data?.data?.parts_classification,
        size: data?.data?.size,
        quality_rank: data?.data?.quality_rank,
        parts_manufacturer_parts_no: data?.data?.parts_manufacturer_parts_no,
        parts_no: String(data?.data?.parts_number),
        parts_id: String(data?.selectedParts?.id),
        body_parts_number: data?.data?.body_parts_number,
        clutch_parts_no: data?.data?.clutch_parts_number,
        rebuilt_manufacturer_parts_no: data.data?.rebuilt_parts_number,
        supplier_id: data?.data?.supplier_id,
        supplier_item_key: data?.data?.supplier_item_key,
        quantity: data?.data?.quantity,
        status: data?.data?.status,
        parts_information: data?.data?.partInfo,
        injury_information: data?.data?.partStatus,
        item_info_1: data?.data?.item_info_1,
        item_info_2: data?.data?.item_info_2,
        color_id: data?.data?.color?.id,
        parts_cost: data?.data?.parts_cost,
        sales_price: data?.data?.sales_price,
        guarantee: data?.data?.guarantee,
        core_return: data?.data?.core_return,
        exhibitor_user_id: data?.exhibitorUserId,
        shipping_from_prefecture: data?.data?.prefecture,
        is_yamato: data?.data?.is_yamato,
        shipping_cutoff_time: data?.data?.shipping_cutoff_time,
        shipping_required_days_from: data?.data?.shipping_required_days_from,
        shipping_required_days_to: data?.data?.shipping_required_days_to,
        shipping_costs: buildShippingCostParams(data?.data?.shipping_costs ?? []),
        item_images: data?.itemImages,
        delete_images: data?.deleteImages,
      };

      const res = await putApi(`${BASE_URL}/items/${data.id}`, params);

      dispatch(
        enqueueSnackbar({
          message: '商品情報の更新が完了しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return {
        id: data.id,
        products: res.data.data,
      };
    } catch (error) {
      console.error(error);
      dispatch(
        enqueueSnackbar({
          message: '商品情報の更新に失敗しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * 自動更新ガードフラグを設定・解除する
 */
export const updateAutoUpdateLock = createAsyncThunk(
  'products/updateAutoUpdateLock',
  async (data, { dispatch }) => {
    try {
      const res = await putApi(`${BASE_URL}/items/${data.id}/auto_update_lock`, {
        is_auto_update_locked: data.is_auto_update_locked,
      });

      dispatch(
        enqueueSnackbar({
          message: res.is_auto_update_locked
            ? '自動更新ガードを設定しました。'
            : '自動更新ガードを解除しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return {
        id: res.id,
        is_auto_update_locked: res.is_auto_update_locked,
      };
    } catch (error) {
      console.error(error);
      dispatch(
        enqueueSnackbar({
          message: '自動更新ガードの更新に失敗しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * リビルト以外を登録する
 */
export const createNonRebuilt = createAsyncThunk(
  'products/createNonRebuilt',
  async (data, { dispatch }) => {
    const { navigate } = data;
    // 生産年式を年と月に分割（例: "H30/1" → year="H30", month="1"）
    const [modelYear, modelMonth] = data?.data?.model_year?.split('/') || ['', ''];

    const params = {
      item_name: data?.data.item_name,
      car_model_designation_no: data?.data?.car_model_designation_no,
      car_model_classification: data?.data?.classification_no,
      car_model_name: data?.carsModel?.name,
      car_model_id: data?.carsModel?.id,
      maker_id: data?.data?.maker,
      car_id: data?.data?.car_name,
      vehicle_no: data?.data?.vehicle_no,
      model_year: modelYear,
      model_month: modelMonth,
      color: data?.data?.color?.id,
      trim: data?.data?.trim,
      grade_name: data?.data?.grade,
      engine: data?.engine?.id,
      mission: data?.data?.mission,
      drive_method: data?.data?.drive_method,
      frame: data?.data?.frame,
      mileage: data?.data?.mileage,
      // data.data.registrationDate
      parts_classification: data?.data?.parts_classification,
      size: data?.data?.size,
      quality_rank: data?.data?.quality_rank,
      parts_manufacturer_parts_no: data?.data?.parts_manufacturer_parts_no,
      parts_no: String(data?.data.parts_number),
      parts_id: String(data?.selectedParts?.id),
      body_parts_number: data?.data?.body_parts_number,
      clutch_parts_no: data?.data?.clutch_parts_number,
      rebuilt_manufacturer_parts_no: data.data?.rebuilt_parts_number,
      supplier_id: data?.data?.supplier_id,
      supplier_item_key: data?.data?.supplier_item_key,
      quantity: data?.data?.quantity,
      status: data?.data?.status,
      parts_information: data?.data?.partInfo,
      injury_information: data?.data?.partStatus,
      item_info_1: data?.data?.item_info_1,
      item_info_2: data?.data?.item_info_2,
      color_id: data?.data?.color?.id,
      parts_cost: data?.data?.parts_cost,
      sales_price: data?.data?.sales_price,
      guarantee: data?.data?.guarantee,
      core_return: data?.data?.core_return,
      exhibitor_user_id: data?.exhibitorUserId,
      shipping_from_prefecture: data?.data?.prefecture,
      is_yamato: data?.data?.is_yamato,
      shipping_cutoff_time: data?.data?.shipping_cutoff_time,
      shipping_required_days_from: data?.data?.shipping_required_days_from,
      shipping_required_days_to: data?.data?.shipping_required_days_to,
      postage: data?.data?.shipping_category || undefined,
      shipping_costs: buildShippingCostParams(data?.data?.shipping_costs ?? []),
      item_images: data?.itemImages,
      duplicate_from_item_id: data?.duplicateFromItemId ?? null,
      exclude_image_ids: data?.excludeImageIds ?? [],
    };

    try {
      const res = await postApi(`${BASE_URL}/items`, params);

      dispatch(
        enqueueSnackbar({
          message: '商品を追加しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );
      navigate(PRODUCT_LIST_VIEW);

      return {
        products: res.data,
      };
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * リビルト品を登録する
 */
export const createRebuiltProduct = createAsyncThunk(
  'products/createRebuiltProduct',
  async (data, dispatch) => {
    const itemImages =
      data?.file?.length > 0
        ? data?.file?.map((location) => ({
            Location: location,
            ETag: '\\97669b1c7265cd25f2c0c53f350204ab\\',
            key: '2/ダウンロード.jpeg',
            Bucket: 'upload-avatar-test',
          }))
        : [
            {
              ETag: '\\97669b1c7265cd25f2c0c53f350204ab\\',
              Location:
                'https://upload-avatar-test.s3.ap-northeast-1.amazonaws.com/1/%E3%82%BF%E3%82%99%E3%82%A6%E3%83%B3%E3%83%AD%E3%83%BC%E3%83%88%E3%82%99.jpeg',
              key: '1/ダウンロード.jpeg',
              Key: '1/ダウンロード.jpeg',
              Bucket: 'upload-avatar-test',
            },
          ];
    const params = {
      item_name: data.data.item_name,
      item_control_no: data.data?.item_no,
      parts_no: data.data?.Uzone_no,
      parts_manufacturer_no: data.data?.true_code,
      car_model_name: data.carsModel?.name,
      car_model_id: data.carsModel?.id,
      maker: data.data?.maker,
      car_name: data.data?.carsCategory,
      car_model: data.data?.carModel,
      parts_id: data?.selectedParts?.id,
      // リビルトは0、中古は1
      parts_classification: 0,
      size: 'S',
      body_parts_no: data.data?.car_no,
      clutch_parts_no: data.data?.clutch,
      rebuilt_manufacturer_parts_no: data.data?.rebuild_no,
      parts_information: data.data?.partsInfo,
      scheduled_delivery_date: data.data?.deliver,
      parts_cost: data.data?.partsPurchasingPrice,
      sales_price: data.data?.basePrice,
      shipping_cutoff_time: data.data?.shippingDeadline,
      guarantee: data.data?.guarantee,
      core_return: data.data?.core_return,
      quantity: data.data?.quantity,
      status: data.data?.status,
      rebuilt_maker_id: data.data?.seller,
      is_yamato: data.data?.deliveryInfo === 1 ? 1 : 0,
      item_images: itemImages,
    };

    try {
      const res = await postApi(`${BASE_URL}/items/rebuilt`, params);

      dispatch(
        enqueueSnackbar({
          message: '商品を追加しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return {
        products: res.data,
      };
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * コメントを作成する
 */
export const createComment = createAsyncThunk(
  'products/createComment',
  async ({ itemId, comment }, { dispatch }) => {
    try {
      const params = {
        item_id: itemId,
        user_id: 1,
        comment,
      };

      const res = await postApi(`${BASE_URL}/items/comment `, params);

      dispatch(
        enqueueSnackbar({
          message: 'コメントを追加しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return {
        data: res.data,
      };
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

export const fetchNonRebuiltStatus = createAsyncThunk(
  'requests/fetchNonRebuiltStatus',
  async () => {
    const res = await getApi(`${BASE_URL}/items/options/non_rebuilt`);

    console.log('requestAPI : 接続', res);

    return { list: res.meta };
  },
);

/**
 * 商品一括更新用の件数を取得する
 */
export const fetchBulkItemCount = createAsyncThunk(
  'products/fetchBulkItemCount',
  async (filterParams, { dispatch }) => {
    try {
      const res = await getApi(`${BASE_URL}/items/bulk/count`, filterParams);

      return res;
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: '件数の取得に失敗しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );
      throw error;
    }
  },
);

/**
 * 商品一括更新用のCSVをダウンロードする（POST: item_ids or filters）
 */
export const fetchBulkProductsCsv = createAsyncThunk(
  'products/fetchBulkProductsCsv',
  async (params, { dispatch }) => {
    try {
      const res = await postApi(`${BASE_URL}/items/bulk/csv`, params);

      return { data: res.data || res };
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'CSVのダウンロードに失敗しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );
      throw error;
    }
  },
);

/**
 * 商品一括更新CSVをアップロードして処理する
 */
export const processBulkProductsCsv = createAsyncThunk(
  'products/processBulkProductsCsv',
  async ({ file, force = false }, { dispatch, rejectWithValue }) => {
    const formData = new FormData();
    formData.append('csv_file', file);
    if (force) {
      formData.append('force', '1');
    }

    try {
      const res = await postApi(`${BASE_URL}/items/bulk/csv_process`, formData);
      dispatch(
        enqueueSnackbar({
          message: `一括更新が完了しました。（${res.updated_count}件更新）`,
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return {
        success: res?.success,
        updated_count: res?.updated_count,
      };
    } catch (e) {
      if (rejectWithValue(e).payload.response.status === HTTP_STATUS.INTERNAL_SERVER_ERROR) {
        dispatch(
          enqueueSnackbar({
            message: 'CSVの一括処理に失敗しました。',
            options: {
              key: new Date().getTime() + Math.random(),
              variant: 'error',
            },
          }),
        );
      }

      return rejectWithValue(e).payload.response.data;
    }
  },
);

/**
 * 商品一覧（画像付き）を取得する
 */
export const fetchBulkListItems = createAsyncThunk(
  'products/fetchBulkListItems',
  async (params, { dispatch }) => {
    try {
      const res = await getApi(`${BASE_URL}/items/bulk/list`, params);

      return res;
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: '商品一覧の取得に失敗しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );
      throw error;
    }
  },
);

const HTTP_INTERNAL_SERVER_ERROR = 500;
const BULK_IMAGE_SUCCESS_MESSAGE = '画像一括更新が完了しました。';
const BULK_IMAGE_ERROR_MESSAGE = '画像一括更新に失敗しました。';

/**
 * 商品画像一括更新（ZIP + mode）
 */
export const processBulkImages = createAsyncThunk(
  'products/processBulkImages',
  async ({ formData }, { dispatch, rejectWithValue }) => {
    try {
      const res = await postApi(`${BASE_URL}/items/bulk/images`, formData);

      const message =
        res.updated_count > 0
          ? `${BULK_IMAGE_SUCCESS_MESSAGE}（${res.updated_count}商品更新）`
          : BULK_IMAGE_SUCCESS_MESSAGE;

      dispatch(
        enqueueSnackbar({
          message,
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return res;
    } catch (e) {
      const status = e?.response?.status;
      if (status === HTTP_INTERNAL_SERVER_ERROR) {
        dispatch(
          enqueueSnackbar({
            message: BULK_IMAGE_ERROR_MESSAGE,
            options: {
              key: new Date().getTime() + Math.random(),
              variant: 'error',
            },
          }),
        );
      }

      return rejectWithValue(e?.response?.data || { message: BULK_IMAGE_ERROR_MESSAGE });
    }
  },
);

const productsAdapter = createEntityAdapter({
  selectId: (e) => e.id,
});

export const productSlice = createSlice({
  name: 'products',
  initialState: productsAdapter.getInitialState({
    loading: false,
    // 自動更新ガード更新中フラグ。loading と分けることで商品詳細画面全体のスピナー表示を避ける
    autoUpdateLockLoading: false,
  }),
  reducers: {
    addTag: productsAdapter.addOne,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllProducts.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchAllProducts.fulfilled, (state, action) => {
      productsAdapter.setAll(state, action.payload.data);
      state.loading = false;
    });
    builder.addCase(fetchAllProducts.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(fetchNonRebuiltProduct.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchNonRebuiltProduct.fulfilled, (state, action) => {
      productsAdapter.upsertOne(state, action.payload.data);
      state.loading = false;
    });
    builder.addCase(fetchRebuiltProduct.fulfilled, (state, action) => {
      productsAdapter.upsertOne(state, action.payload.data);
      state.loading = false;
    });
    builder.addCase(fetchRebuiltProduct.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateProduct.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateProduct.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(updateProduct.fulfilled, (state, action) => {
      productsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: action.payload.products,
      });
      state.loading = false;
    });
    builder.addCase(updateAutoUpdateLock.pending, (state) => {
      state.autoUpdateLockLoading = true;
    });
    builder.addCase(updateAutoUpdateLock.rejected, (state) => {
      state.autoUpdateLockLoading = false;
    });
    builder.addCase(updateAutoUpdateLock.fulfilled, (state, action) => {
      productsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: { is_auto_update_locked: action.payload.is_auto_update_locked },
      });
      state.autoUpdateLockLoading = false;
    });
    builder.addCase(createNonRebuilt.fulfilled, (state, action) => {
      productsAdapter.addOne(state, action.payload.products);
      state.loading = false;
    });
    builder.addCase(createNonRebuilt.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createRebuiltProduct.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createRebuiltProduct.fulfilled, (state, action) => {
      productsAdapter.addOne(state, action.payload.products);
      state.loading = false;
    });
    builder.addCase(createComment.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createComment.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(createComment.fulfilled, (state) => {
      state.loading = false;
    });
  },
});

export const productSelectors = productsAdapter.getSelectors((state) => state.products);

export default productSlice.reducer;
